import { Button, CenterAlignedLoader, removeAllToasts } from '@medly-components/core';
import { PersonPinCircleIcon } from '@medly-components/icons';
import { useRegionContext } from 'context/RegionContext';
import { UserContext } from 'context/UserContext';
import { ZoneContext } from 'context/ZoneContext';
import { useBoolean, useIsMobile } from 'hooks';
import { drawerActions, drawerEventParams, eventCategories } from 'hooks/useAnalytics/constants';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';
import { useMantaCareAPI } from 'hooks/useMantaCareAPI/useMantaCareAPI';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ReactPlayerProps, default as _ReactPlayer } from 'react-player';
import { Container, Content, DrawerWrapper, Footer, Loader } from './Drawer.styled';
import DrawerHeader from './components/DrawerHeader';
import DrawerSection from './components/DrawerSection';
import Source from './components/Source';
import { DrawerData } from './types';
import { getContentWithSearchHighlited } from 'pages/MapSearch/utils';
import useMapSearch from 'hooks/search/useMapSearch';
import Resources from './components/Resources';
import { Feedback } from './components/Feedback/Feedback';
const ReactPlayer = _ReactPlayer as unknown as React.FC<ReactPlayerProps>;

type DrawerContent = {
    data: DrawerData;
};

const DrawerContent = ({ data }: DrawerContent) => {
    const { assetLocation, assetDescription, questions, considerations, paragraphs, resources } = data;
    const filteredResources = resources?.filter(({ link }) => link);
    const { region } = useRegionContext();
    const { zone, selectedStation } = useContext(ZoneContext);
    const { getSearchWords } = useMapSearch();
    const searchWords = selectedStation ? getSearchWords(selectedStation.id) : [];

    const { captureMantaCareEvent } = useAnalytics();

    const onVideoStart = () => {
        captureMantaCareEvent({
            type: 'map',
            action: 'drawer_link_interaction',
            metadata: {
                region: {
                    id: region.id,
                    name: region.name
                },
                map: { name: region.map.name },
                zone: { name: zone.name },
                station: {
                    id: selectedStation!.id,
                    name: selectedStation!.name,
                    type: selectedStation!.type,
                    description: selectedStation!.description
                },
                type: 'video',
                name: assetDescription!,
                link: assetLocation!
            }
        });
    };

    return (
        <Content>
            {paragraphs?.length
                ? paragraphs.map(({ header, text, sources }) => {
                      return text ? (
                          <DrawerSection key={header} header={header}>
                              <div
                                  dangerouslySetInnerHTML={{
                                      __html: getContentWithSearchHighlited(text.replace(/\n/g, '<br />'), searchWords)
                                  }}
                              ></div>
                              {Array.isArray(sources) ? (
                                  sources.map(source => <Source key={source.link} {...source} />)
                              ) : (
                                  <Source {...sources} />
                              )}
                          </DrawerSection>
                      ) : null;
                  })
                : null}

            {questions?.length ? (
                <DrawerSection header="Questions for the medical care team">
                    <ol style={{ listStyleType: 'none' }}>
                        {questions.split('\n').map(question => {
                            return (
                                <li
                                    key={question}
                                    dangerouslySetInnerHTML={{ __html: getContentWithSearchHighlited(question, searchWords) }}
                                ></li>
                            );
                        })}
                    </ol>
                </DrawerSection>
            ) : null}

            {considerations?.length ? (
                <DrawerSection header="Considerations for decision making:">
                    <ol style={{ listStyleType: 'none' }}>
                        {considerations.split('\n').map(consideration => {
                            return (
                                <li
                                    key={consideration}
                                    dangerouslySetInnerHTML={{ __html: getContentWithSearchHighlited(consideration, searchWords) }}
                                ></li>
                            );
                        })}
                    </ol>
                </DrawerSection>
            ) : null}
            {assetLocation ? (
                <DrawerSection header={getContentWithSearchHighlited(assetDescription || '', searchWords) ?? ''}>
                    <ReactPlayer
                        data-testid="reactplayer"
                        controls
                        width="100%"
                        height="26rem"
                        url={assetLocation}
                        onStart={onVideoStart}
                    />
                </DrawerSection>
            ) : null}
            <Resources resources={filteredResources} stationName={data.stationName} />
        </Content>
    );
};

type Drawer = {
    onClose?: () => void;
};
export const Drawer = ({ onClose }: Drawer) => {
    const [data, setData] = useState<DrawerData>();
    const [open, setOpen] = useState(true);
    const isMobile = useIsMobile();
    const { markMyPosition, getDrawer, error, loading } = useMantaCareAPI();
    const { user } = useContext(UserContext);
    const { region, setRegion } = useRegionContext();
    const { zone, setSelectedStation, selectedStation } = useContext(ZoneContext);
    const { captureEvent, captureMantaCareEvent } = useAnalytics();
    const { value: isOpenFeedbackForm, setTrue: openFeedbackForm, setFalse: closeFeedbackForm } = useBoolean();

    const getDrawerData = async () => {
        if (selectedStation) {
            const response = await getDrawer(selectedStation.id);
            if (!error && response) {
                captureEvent(eventCategories.DRAWER, drawerActions.OPEN, null, {
                    [drawerEventParams.STATION_ID]: selectedStation.id,
                    [drawerEventParams.STATION_NAME]: response.stationName,
                    [drawerEventParams.STATION_DESCRIPTION]: selectedStation.description,
                    [drawerEventParams.REGION_NAME]: region.name,
                    [drawerEventParams.ZONE_NAME]: zone.name
                });
                setData(response);
            }
        }
    };
    useEffect(() => {
        getDrawerData();
    }, [selectedStation?.id]);

    useEffect(() => {
        return () => removeAllToasts();
    }, []);

    const closeDrawer = useCallback(() => {
        setOpen(false);
        onClose && onClose();
    }, [onClose]);

    const handleClose = () => {
        captureMantaCareEvent({
            type: 'map',
            action: 'drawer_close',
            metadata: {
                region: { id: region.id, name: region.name },
                map: { name: region.map.name },
                zone: { name: zone.name },
                station: {
                    id: selectedStation!.id,
                    name: selectedStation!.name,
                    type: selectedStation!.type,
                    description: selectedStation!.description
                }
            }
        });
        closeDrawer();
        !isMobile && setSelectedStation(undefined);
    };

    /**
     * When we reset selectedStation drawer will be completely removed
     * It is closing without animation so waiting for animation to end here
     */
    const onDrawerAnimationEnd = useCallback(() => {
        if (!open) {
            setSelectedStation(undefined);
        }
    }, [open, setSelectedStation]);

    /**
     * Triggers mark my position API with selected station and zone
     * Shows loader during API call
     * Shows error toast if any api failure
     * Updates region data received in api
     * Resets selected station and closes the drawer
     */
    const onUpdateLocation = useCallback(async () => {
        if (!user || !selectedStation) {
            return;
        }
        const regionId = zone.ref ? zone.ref.regionId : region.id;
        const response = await markMyPosition(user.id, regionId, {
            station: selectedStation.id,
            zone: zone.name
        });
        if (!error && response) {
            captureMantaCareEvent({
                type: 'map',
                action: 'i_am_here',
                metadata: {
                    region: { id: region.id, name: region.name },
                    map: { name: region.map.name },
                    zone: { name: zone.name },
                    station: {
                        id: selectedStation!.id,
                        name: selectedStation!.name,
                        type: selectedStation!.type,
                        description: selectedStation!.description
                    }
                }
            });
            setRegion({
                ...region,
                position: {
                    station: selectedStation.id,
                    zone: zone.name
                }
            });
        }
        closeDrawer();
    }, [selectedStation?.id, zone.name, captureMantaCareEvent]);

    if (!selectedStation) {
        return null;
    }

    return (
        <>
            <Container>
                {data && loading ? <Loader id="update-location-loader" data-testid="update-location-loader" /> : null}
                <DrawerWrapper
                    position="right"
                    onClose={handleClose}
                    onAnimationEnd={onDrawerAnimationEnd}
                    open={open}
                    width={isMobile ? '100%' : '54rem'}
                >
                    <DrawerHeader updatedAt={data?.updatedAt} />
                    {data ? <DrawerContent data={data} /> : <CenterAlignedLoader />}
                    <Footer alignItems="center">
                        <Button edges="square" fullWidth size="M" type="button" variant="outlined" onClick={openFeedbackForm}>
                            Make a Suggestion
                        </Button>
                        <Button edges="square" fullWidth size="M" type="button" variant="solid" onClick={onUpdateLocation}>
                            <PersonPinCircleIcon /> I am here
                        </Button>
                    </Footer>
                </DrawerWrapper>
            </Container>
            {isOpenFeedbackForm ? <Feedback onClose={closeFeedbackForm} /> : null}
        </>
    );
};
